import { useState, useEffect, useRef } from 'react';
import * as api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';

const STORAGE_KEY = 'feature-request-chat';
const WELCOME = {
  role: 'assistant',
  content: "Have a feature idea or found something that could be better? Describe it here and I'll help turn it into a clear request.",
};

const STATUS_LABELS = { new: 'New', reviewed: 'Reviewed', planned: 'Planned', declined: 'Declined' };
const STATUS_COLORS = { new: 'info', reviewed: 'warning', planned: 'success', declined: 'danger' };
const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High' };
const PRIORITY_COLORS = { low: '', medium: 'warning', high: 'danger' };
const CATEGORY_LABELS = { ui: 'UI', feature: 'Feature', integration: 'Integration', performance: 'Performance', bug: 'Bug', other: 'Other' };

const fmtDate = (d) => new Date(d).toLocaleDateString('en-CA');

const FeatureRequests = () => {
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [messages, setMessages] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
      return saved.length ? saved : [WELCOME];
    } catch { return [WELCOME]; }
  });
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { loadSubmissions(); }, []);

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages)); } catch { /* ignore */ }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSubmissions = async () => {
    try {
      const r = await api.getFeatureRequests();
      setSubmissions(r.data);
    } catch { /* ignore */ } finally {
      setLoadingSubmissions(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    const userMsg = { role: 'user', content: text };
    const history = messages.filter((m) => m.role !== 'system');
    const nextMessages = [...history, userMsg];
    setMessages([...messages, userMsg, { role: 'assistant', content: '' }]);
    setInput('');
    setThinking(true);
    setPendingRequest(null);

    try {
      const response = await fetch('/api/intake/feature-request-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nextMessages.map((m) => ({ role: m.role, content: m.content })) }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(line.slice(6)); } catch { continue; }
          if (evt.type === 'text') {
            streamedText += evt.text;
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: streamedText };
              return updated;
            });
          } else if (evt.type === 'done') {
            setThinking(false);
            if (evt.done && evt.project) setPendingRequest(evt.project);
          } else if (evt.type === 'error') {
            setThinking(false);
            showToast(evt.message || 'AI error', 'error');
          }
        }
      }
    } catch (err) {
      setThinking(false);
      showToast(err.message || 'Connection error', 'error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const saveRequest = async () => {
    if (!pendingRequest || saving) return;
    setSaving(true);
    try {
      await api.createFeatureRequest(pendingRequest);
      showToast('Feature request submitted — thanks!', 'success');
      setPendingRequest(null);
      setMessages([WELCOME]);
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      loadSubmissions();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const discardPending = () => {
    setPendingRequest(null);
    inputRef.current?.focus();
  };

  const removeSubmission = async (sub) => {
    if (!(await confirm({
      title: 'Delete request',
      message: `Delete "${sub.title}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    try {
      await api.deleteFeatureRequest(sub._id);
      setSubmissions((prev) => prev.filter((s) => s._id !== sub._id));
      showToast('Request deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const clearChat = () => {
    setPendingRequest(null);
    setMessages([WELCOME]);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Feature Requests</h1>
          <p className="page-subtitle">Share ideas or improvements — AI helps you articulate them clearly.</p>
        </div>
      </div>

      {/* AI Chat */}
      <div className="fr-chat-panel">
        <div className="fr-chat-header">
          <span className="fr-chat-title">Describe Your Idea</span>
          {messages.length > 1 && (
            <button className="btn btn-secondary btn-sm" onClick={clearChat}>Clear</button>
          )}
        </div>

        <div className="fr-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`fr-message fr-message-${msg.role}`}>
              {msg.content || (thinking && i === messages.length - 1
                ? <span className="fr-thinking">thinking…</span>
                : null)}
            </div>
          ))}
          {pendingRequest && (
            <div className="fr-pending-card">
              <p className="fr-pending-label">Ready to submit:</p>
              <p className="fr-pending-title">{pendingRequest.title}</p>
              {pendingRequest.description && (
                <p className="fr-pending-desc">{pendingRequest.description}</p>
              )}
              <div className="fr-pending-meta">
                <span className={`status-badge status-${STATUS_COLORS[pendingRequest.status] || 'info'}`}>
                  {CATEGORY_LABELS[pendingRequest.category] || pendingRequest.category}
                </span>
                <span className={`status-badge status-${PRIORITY_COLORS[pendingRequest.priority] || 'info'}`}>
                  {PRIORITY_LABELS[pendingRequest.priority] || pendingRequest.priority} priority
                </span>
              </div>
              <div className="fr-pending-actions">
                <button className="btn btn-primary" onClick={saveRequest} disabled={saving}>
                  {saving ? 'Saving…' : 'Submit Request'}
                </button>
                <button className="btn btn-secondary" onClick={discardPending}>Keep chatting</button>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="fr-input-row">
          <textarea
            ref={inputRef}
            className="fr-input"
            rows={2}
            placeholder="Describe a feature idea or improvement…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={thinking}
          />
          <button className="btn btn-primary fr-send-btn" onClick={send} disabled={thinking || !input.trim()}>
            {thinking ? '…' : 'Send'}
          </button>
        </div>
      </div>

      {/* Submissions */}
      <section className="fr-submissions">
        <h2>My Submissions</h2>
        {loadingSubmissions ? (
          <p className="admin-muted">Loading…</p>
        ) : submissions.length === 0 ? (
          <p className="admin-muted">No submissions yet.</p>
        ) : (
          <div className="fr-submission-list">
            {submissions.map((sub) => (
              <div key={sub._id} className="fr-submission-card">
                <div className="fr-submission-header">
                  <span className="fr-submission-title">{sub.title}</span>
                  <div className="fr-submission-badges">
                    <span className={`status-badge status-${PRIORITY_COLORS[sub.priority] || ''}`}>
                      {PRIORITY_LABELS[sub.priority]}
                    </span>
                    <span className={`status-badge status-${STATUS_COLORS[sub.status]}`}>
                      {STATUS_LABELS[sub.status]}
                    </span>
                  </div>
                </div>
                {sub.description && <p className="fr-submission-desc">{sub.description}</p>}
                {sub.adminResponse && (
                  <div className="fr-admin-response">
                    <span className="fr-admin-response-label">Response:</span> {sub.adminResponse}
                  </div>
                )}
                <div className="fr-submission-footer">
                  <span className="fr-submission-meta">
                    {CATEGORY_LABELS[sub.category]} · {fmtDate(sub.createdAt)}
                  </span>
                  {sub.status === 'new' && (
                    <button className="btn btn-danger btn-sm" onClick={() => removeSubmission(sub)}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default FeatureRequests;
